/**
 * XdcSupplyBreakdown — V2.K.9
 *
 * Renders a supply breakdown for XDC on the XdcMonitor page.
 *
 * Why XDC has its own story :
 *  - **Pre-mine 37.5 B** at mainnet genesis (May 2019). No vesting
 *    escrow — tokens were progressively distributed from the genesis
 *    wallet to Foundation / founders / ecosystem pools.
 *  - **Inflation post-genesis** : 5.5 XDC per ~2-second block →
 *    ~86.7 M XDC/year minted as Masternode block rewards.
 *  - **No hard cap** — XDC is structurally inflationary, but the
 *    network burns **20 %** of all smart-contract transaction fees
 *    to counter that. Net direction depends on transaction volume.
 *  - **108 Masternodes** secure the network on a Delegated Proof of
 *    Stake consensus (Validator + Standby). 10 M XDC min stake.
 *    XDC 2.0 upgraded BFT + forensics.
 */

import { useTranslation } from 'react-i18next';
import { useXdcSupply } from '../../hooks/useXdcSupply';

const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'docs' | 'forum' | 'analysis';
  note: string;
}> = [
  {
    label: 'XDC Node Architecture (docs officielles)',
    url: 'https://docs.xdc.network/xdcchain/developers/node_operators/node_architecture/',
    category: 'docs',
    note: 'Architecture des Masternodes + DPoS + block rewards.',
  },
  {
    label: 'XDC 2.0 Staking, Rewards and Burning Upgrade',
    url: 'https://www.xdc.dev/riteshkakkad/xdc-20-staking-rewards-and-burning-upgrade-284',
    category: 'forum',
    note: 'Post officiel du fondateur sur les mécaniques rewards + burn XDC 2.0.',
  },
  {
    label: 'XDC Tokenomics — MEXC',
    url: 'https://www.mexc.com/price/XDC/tokenomics',
    category: 'analysis',
    note: 'Métriques supply, market cap, vesting schedule.',
  },
  {
    label: 'XDC Network — CoinGecko',
    url: 'https://www.coingecko.com/en/coins/xdc-network',
    category: 'analysis',
    note: 'Données live circulating / total supply (source de ce composant).',
  },
];

export function XdcSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useXdcSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;
  const fmtM = (n: number) =>
    `${(n / 1_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} M`;

  return (
    <section
      aria-labelledby="xdc-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="xdc-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#244B85' }}>◇</span>
          {t('xdc.supply.title', 'Supply XDC — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'xdc.supply.subtitle',
            'Pré-mine 37,5 Md au genesis 2019 + inflation continue Masternodes (~86,7 M XDC/an) − burn 20 % des fees smart-contract. Trajectoire nette dépend du volume on-chain.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtB={fmtB} fmtM={fmtM} t={t} />
          <MintBurnVisual data={data} fmtM={fmtM} t={t} />
          <Legend t={t} />
          <MasternodeNote t={t} fmt={fmt} fmtM={fmtM} data={data} />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useXdcSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  fmtM: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtB, fmtM, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('xdc.supply.circulating', 'En circulation')}
        value={fmtB(data.circulating)}
        sub={`${fmt(data.circulating)} XDC`}
        color="#244B85"
      />
      <StatCard
        label={t('xdc.supply.total', 'Supply totale')}
        value={fmtB(data.total)}
        sub={t('xdc.supply.totalNote', 'Pré-mine + inflation Masternodes')}
        color="#3b82f6"
      />
      <StatCard
        label={t('xdc.supply.annualMint', 'Mint annuel (block rewards)')}
        value={`~${fmtM(data.annualMint)}`}
        sub={t('xdc.supply.annualMintNote', '5,5 XDC × bloc 2 s')}
        color="#f59e0b"
      />
      <StatCard
        label={t('xdc.supply.feeBurn', 'Burn (fees smart-contract)')}
        value={`${data.feeBurnPct} %`}
        sub={t('xdc.supply.feeBurnNote', 'Contrepoids déflationniste à l\'inflation')}
        color="#10b981"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mint vs Burn visual : the mint side is a fixed annual quantity,
// the burn side depends on transaction volume — we visualise mint as
// a known bar and burn as a "potential" segment dependent on usage.
// ─────────────────────────────────────────────────────────────────────

function MintBurnVisual({
  data,
  fmtM,
  t,
}: {
  data: NonNullable<ReturnType<typeof useXdcSupply>['data']>;
  fmtM: (n: number) => string;
  t: (key: string, fallback: string) => string;
}) {
  return (
    <div
      style={{
        marginBottom: '8px',
        padding: '10px 12px',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
      }}
      aria-label={t(
        'xdc.supply.barAlt',
        'Comparaison structurelle mint (block rewards constants) vs burn (20 % des fees smart-contract)',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('xdc.supply.mintSide', 'Mint : block rewards Masternodes')}
          </div>
          <div
            style={{
              height: '14px',
              borderRadius: '6px',
              background: 'linear-gradient(90deg, #f59e0b, rgba(245, 158, 11, 0.25))',
              position: 'relative',
            }}
          >
            <span
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '10px',
                fontWeight: 700,
                color: 'white',
              }}
            >
              +~{fmtM(data.annualMint)} / an
            </span>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('xdc.supply.burnSide', 'Burn : {{pct}} % des fees smart-contract').replace('{{pct}}', String(data.feeBurnPct))}
          </div>
          <div
            style={{
              height: '14px',
              borderRadius: '6px',
              background: 'linear-gradient(90deg, #10b981, rgba(16, 185, 129, 0.25))',
              position: 'relative',
            }}
          >
            <span
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '10px',
                fontWeight: 700,
                color: 'white',
              }}
            >
              −{data.feeBurnPct} %
            </span>
          </div>
        </div>
      </div>
      <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.5 }}>
        {t(
          'xdc.supply.visualNote',
          'Le mint est constant (block rewards prévisibles), le burn dépend du volume on-chain. XDC devient net déflationniste quand l\'activité smart-contract génère des fees burnés > 86,7 M XDC/an d\'inflation.',
        )}
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#f59e0b" label={t('xdc.supply.legendMint', 'Mint — 5,5 XDC × bloc 2 s (Masternodes)')} />
      <LegendItem color="#10b981" label={t('xdc.supply.legendBurn', 'Burn — 20 % des fees smart-contract')} />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Masternode callout
// ─────────────────────────────────────────────────────────────────────

function MasternodeNote({
  t,
  fmt,
  fmtM,
  data,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  fmtM: (n: number) => string;
  data: NonNullable<ReturnType<typeof useXdcSupply>['data']>;
}) {
  const body = t(
    'xdc.supply.masternodeBody',
    "Le consensus DPoS de XDC repose sur {{slots}} Masternodes ({{validators}} Validator + Standby). Pour en opérer un, il faut staker un minimum de {{minStake}} XDC, ce qui garantit l'alignement économique des opérateurs avec la sécurité du réseau. Chaque bloc de ~2 s mint 5,5 XDC distribués aux Masternodes (~{{annualMint}} XDC mintés par an, APY actuel 5-7 %). XDC 2.0 a apporté un consensus BFT renforcé + forensics, avec des epoch pools de 6000 XDC répartis sur 108 slots.",
  )
    .replace('{{slots}}', String(data.masternodeSlots))
    .replace('{{validators}}', String(data.masternodeSlots))
    .replace('{{minStake}}', fmt(data.masternodeMinStake))
    .replace('{{annualMint}}', fmtM(data.annualMint));

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(36, 75, 133, 0.08)',
        border: '1px dashed rgba(36, 75, 133, 0.40)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#7da6f0' }}>
        {t('xdc.supply.masternodeTitle', 'Masternodes & XDC 2.0 — DPoS + BFT')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'xdc.supply.masternodeSource',
          'Sources : docs.xdc.network/node_architecture, xdc.dev (post fondateur XDC 2.0).',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('xdc.supply.knownEndpointsTitle', 'Sources officielles & docs')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'xdc.supply.knownEndpointsDisclaimer',
          'Le burn de 20 % des fees smart-contract est documenté par XinFin (xdc.dev / whitepaper XDC 2.0). Le mécanisme exact (déduction protocolaire pure vs transfert vers une adresse nulle) n\'est pas explicitement tranché en source primaire — la doc XDC évoque aussi EIP-1559 mais sur testnet Apothem, donc risque d\'amalgame entre les deux mécaniques. À vérifier dans une XIP ou le code XDPoS si besoin. La supply allocation initiale (Foundation ~15 Md, founders/team ~15 Md, dApp dev ~10 Md) provient du genesis wallet ; une adresse de genesis circule (xdc54d4369719...e5 sur IQ.wiki) mais sans source primaire vérifiable côté XinFin — non hardcodée. Surveiller via les explorers officiels pour des chiffres en live.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'docs' | 'forum' | 'analysis' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    docs: { bg: 'rgba(36, 75, 133, 0.30)', color: '#7da6f0', label: 'Docs' },
    forum: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Forum' },
    analysis: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Analyse' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply XDC sera affichée dès que l'API répondra.
    </div>
  );
}
